import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import { createTransform } from "redux-persist";
import { initialState as initialStudiesSliceState } from "../pages/Studies/states/studiesSlice";
import storage from "redux-persist/lib/storage";
import { injectStore as injectAxiosStore } from "../services/axiosInstance";
import LZString from "lz-string";

import studiesSlice from "../pages/Studies/states/studiesSlice";
import manageDescriptionGuideSlice from "src/pages/ManageStudies/states/slice/manageDescriptionGuideSlice";
import unsavedChangesSlice from "src/pages/ManageStudies/states/slice/unsavedChangesSlice";
import manageStudySlice from "src/pages/ManageStudies/states/slice/manageStudySlice";
import manageTranscriptSlice from "src/pages/ManageStudies/states/slice/manageTranscriptSlice";
import deepDiveInsightslice from "src/pages/Insights/states/deepDiveInsightslice";
import adminCenterSlice from "src/pages/AdminCenter/states/adminCenterSlice";
import themeManagementSlice from "src/pages/AdminCenter/states/themeManagementSlice";
import taxonomySlice from "src/pages/Taxanomy/states/taxonomySlice";
import notesInsightsSlice from "src/pages/NotesInsights/states/notesInsightsSlice";
import studyManagementSlice from "src/pages/AdminCenter/states/studyManagementSlice";
import userManagementSlice from "src/pages/AdminCenter/states/userManagementSlice";
import serviceAccountSlice from "src/pages/AdminCenter/states/serviceAccountSlice";
import userMonitoringSlice from "src/pages/AdminCenter/states/userMonitoringSlice";
import askKuralSlice, { initialState as initialAskKuralSliceState } from "src/pages/NewAskKural/states/askKuralSlice";
import widgetSlice from "src/pages/NewAskKural/states/widgetSlice";
import governanceSlice from "src/pages/NewAskKural/states/governanceSlice";
import executiveSummarySlice from "src/pages/Insights/states/executiveSummarySlice";
import quantSegmentSlice from "src/pages/Insights/states/quantSegmentSlice";
import storyBoardSlice from "src/pages/StoryBoard/states/storyBoardSlice";
import sectionalSummarySlice, { initialState as initialSectionalSummarySliceState } from "src/pages/Insights/states/sectionalSummarySlice";
import countWidgetSlice from "src/pages/NewAskKural/states/countWidgetSlice";
import userGuideSlice from "src/pages/UserGuide/states/userGuideSlice";
import profileContextSlice from "src/pages/ProfileContext/states/profileContextSlice";
import manageApiKeySlice from "src/pages/UserGuide/states/manageApiKeySlice";
import reportsSlice, { initialState as initialReportsSliceState } from "src/pages/Reports/states/reportsSlice";
import websocketTranscriptService from "src/pages/ManageStudies/serviceHelper/socketTranscriptService";
import websocketDiscussionGuideService from "src/pages/ManageStudies/serviceHelper/socketDGService";
import websocketReportService from "src/pages/Reports/serviceHelper/socketReportsService";
import userActivitySlice, { initialState as initialUserActivitySlice } from "src/components/UserMonitoring/states/userActivitySlice";
import personasHomeSlice from "src/pages/PersonasHome/states/personasHomeSlice";
import setupSlice from "src/pages/Setup/states/setupSlice";
import chatSlice from "src/pages/Chat/states/chatSlice";
import dtProjectsSlice from "src/pages/DTProjects/states/dtProjectsSlice";
import dtProjectDetailSlice from "src/pages/DTProjectDetail/states/dtProjectDetailSlice";
import editMetadataSlice from "src/pages/EditMetadata/states/editMetadataSlice";
import { initialState as initialUserMonitoringState } from "../pages/AdminCenter/states/userMonitoringSlice";
import adminTranscriptExportSlice from "src/pages/AdminCenter/states/adminTranscriptExportSlice";
import quantDashboardSlice from "src/pages/Insights/states/quantDashboardSlice";
import customSegmentSlice from "src/pages/Insights/states/customSegmentSlice";

// Compression transform
const compressionTransform = createTransform(
	// Compress on serialize (save to storage)
	(inboundState, key) => {
		try {
			const compressed = LZString.compress(JSON.stringify(inboundState));
			return compressed;
		} catch (error) {
			console.error(`Failed to compress ${String(key)}:`, error);
			return null;
		}
	},
	// Decompress on deserialize (load from storage)
	(outboundState, key) => {
		try {
			if (!outboundState) return null;
			const decompressed = LZString.decompress(outboundState);
			if (!decompressed) return null;
			return JSON.parse(decompressed);
		} catch (error) {
			console.error(`Failed to decompress ${String(key)}:`, error);
			// Clear corrupted data
			localStorage.removeItem(`persist:${String(key)}`);
			return null;
		}
	},
	// Apply to all persisted slices
	{ whitelist: ["studiesSlice", "adminCenterSlice", "reportsSlice", "userActivitySlice", "userMonitoringSlice"] },
);

// Data filtering transform
const dataFilterTransform = createTransform(
	(inboundState: any, key: string) => {
		// Add null/undefined checks
		if (!inboundState) return null;

		if (key === "adminCenterSlice") {
			return {
				adminDropDownMaster: inboundState.adminDropDownMaster,
			};
		}
		if (key === "studiesSlice") {
			return {
				userProfile: inboundState.userProfile,
				userProfileId: inboundState.userProfileId,
				role: inboundState.role,
				selectedStudy: inboundState.selectedStudy,
				supportedLanguages: inboundState.supportedLanguages,
				allNotesTemplates: inboundState.allNotesTemplates,
				allWebBoardsTemplates: inboundState.allWebBoardsTemplates,
				folderDropdownData: inboundState.folderDropdownData,
				allStudyTypes: inboundState.allStudyTypes,
				campaignVisibility: inboundState.campaignVisibility,
				// Dismissal is session-only: do NOT persist to localStorage so the banner reappears on
				// every refresh and the dismissal stays valid only for the current session.
				// appLicenseBannerDismissedFor: inboundState.appLicenseBannerDismissedFor,
			};
		}
		if (key === "sectionalSummarySlice") {
			return {
				allChartsCacheFlat: inboundState.sectionalSummary?.allChartsCacheFlat ?? {},
				allChartsSummaryCacheFlat: inboundState.sectionalSummary?.allChartsSummaryCacheFlat ?? {},
			};
		}
		if (key === "reportsSlice") {
			return {
				selectedReport: inboundState.selectedReport,
				createReportForm: inboundState.createReportForm,
			};
		}
		if (key === "userActivitySlice") {
			return {
				userActivityData: inboundState.userActivityData,
			};
		}
		if (key === "askKuralSlice") {
			return {
				selectedMode: inboundState.selectedMode,
				lastSelectedWidgetType: inboundState.lastSelectedWidgetType,
			};
		}
		if (key === "userMonitoringSlice") {
			return {
				dashboardCache: inboundState.dashboardCache,
				kpiCache: inboundState.kpiCache,
				peakUsageCache: inboundState.peakUsageCache,
				apiTimingCache: inboundState.apiTimingCache,
				lastRefreshedAt: inboundState.lastRefreshedAt,
			};
		}
		return inboundState;
	},
	(outboundState: any, key: string) => {
		// Add null/undefined checks and fallback to initial state
		if (!outboundState) {
			if (key === "studiesSlice") return initialStudiesSliceState;
			// sectionalSummarySlice state is wrapped as { sectionalSummary: IFilterState };
			// returning the bare inner state here would make state.sectionalSummary undefined
			// and crash every reducer in the slice.
			if (key === "sectionalSummarySlice") return { sectionalSummary: initialSectionalSummarySliceState };
			if (key === "reportsSlice") return initialReportsSliceState;
			if (key === "userActivitySlice") return initialUserActivitySlice;
			if (key === "askKuralSlice") return initialAskKuralSliceState;
			if (key === "userMonitoringSlice") return initialUserMonitoringState;
		}

		if (key === "studiesSlice") {
			return {
				...initialStudiesSliceState,
				...outboundState,
			};
		}
		if (key === "adminCenterSlice") {
			return {
				...outboundState,
				adminDropDownMaster: outboundState?.adminDropDownMaster || {},
			};
		}
		if (key === "sectionalSummarySlice") {
			return {
				sectionalSummary: {
					...initialSectionalSummarySliceState,
					...outboundState,
					allChartsCacheFlat: outboundState?.allChartsCacheFlat ?? {},
					allChartsSummaryCacheFlat: outboundState?.allChartsSummaryCacheFlat ?? {},
				},
			};
		}
		if (key === "reportsSlice") {
			return {
				...initialReportsSliceState,
				...outboundState,
				selectedReport: outboundState.selectedReport || {},
				createReportForm: outboundState.createReportForm || {},
			};
		}
		if (key === "userActivitySlice") {
			return {
				...outboundState,
				userActivityData: Array.isArray(outboundState.userActivityData) ? outboundState.userActivityData : [],
			};
		}
		if (key === "askKuralSlice") {
			return {
				...initialAskKuralSliceState,
				...outboundState,
				selectedMode: outboundState?.selectedMode,
				lastSelectedWidgetType: outboundState?.lastSelectedWidgetType,
			};
		}
		if (key === "userMonitoringSlice") {
			return {
				...initialUserMonitoringState,
				...outboundState,
				dashboardCache: outboundState?.dashboardCache ?? {},
				kpiCache: outboundState?.kpiCache ?? {},
				peakUsageCache: outboundState?.peakUsageCache ?? {},
				apiTimingCache: outboundState?.apiTimingCache ?? {},
				lastRefreshedAt: outboundState?.lastRefreshedAt ?? null,
			};
		}
		return outboundState;
	},
	{ whitelist: ["studiesSlice", "adminCenterSlice", "reportsSlice", "userActivitySlice", "askKuralSlice", "personasHomeSlice", "userMonitoringSlice"] },
);

const persistConfig = {
	key: "root",
	storage,
	whitelist: ["studiesSlice", "adminCenterSlice", "reportsSlice", "userActivitySlice", "askKuralSlice", "userMonitoringSlice"],
	transforms: [
		dataFilterTransform, // Apply data filtering first
		compressionTransform, // Then apply compression
	],
	throttle: 2000, // Wait at least 2s between localStorage writes to avoid blocking UI
	migrate: (state: any) => {
		// Handle migration if needed
		return Promise.resolve(state);
	},
};

// Combine all reducers
const rootReducer = combineReducers({
	manageDescriptionGuideSlice,
	unsavedChangesSlice,
	manageStudySlice,
	manageTranscriptSlice,
	studiesSlice,
	askKuralSlice,
	widgetSlice,
	governanceSlice,
	deepDiveInsightslice,
	adminCenterSlice,
	themeManagementSlice,
	notesInsightsSlice,
	taxonomySlice,
	studyManagementSlice,
	userManagementSlice,
	serviceAccountSlice,
	userMonitoringSlice,
	adminTranscriptExportSlice,
	sectionalSummarySlice,
	executiveSummarySlice,
	quantSegmentSlice,
	storyBoardSlice,
	countWidgetSlice,
	reportsSlice,
	userGuideSlice,
	profileContextSlice,
	manageApiKeySlice,
	userActivitySlice,
	personasHomeSlice,
	setupSlice,
	chatSlice,
	quantDashboardSlice,
	customSegmentSlice,
	dtProjectsSlice,
	dtProjectDetailSlice,
	editMetadataSlice,
});

// Create a persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: ["persist/PERSIST", "persist/REHYDRATE", "persist/PURGE"],
			},
		}),
	// DevTools disabled in production to avoid per-action serialization overhead.
	devTools: process.env.NODE_ENV !== "production" && {
		name: "Kural Store",
	},
});

// Create the persistor
export const persistor = persistStore(store);

// Setup services with the Redux store
export const setupServices = () => {
	// Inject store into Axios instance
	injectAxiosStore(store);
	websocketTranscriptService.injectStore(store);
	websocketDiscussionGuideService.injectStore(store);
	websocketReportService.injectStore(store);
};

// Initialize services
setupServices();

// Use `rootReducer` (not `store.getState`) so slice state is fully inferred; `persistReducer` widens many branches to `{}`.
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
